import React, { useState, useMemo, useCallback } from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { LegendList } from '@legendapp/list';
import { useMutation } from '@tanstack/react-query';
import SheetModal from '@/components/General/SheetModal';
import SearchBox from '@/components/General/inputs/SearchBox';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { createAdditionalEventInstance, deleteEvent } from '@/data/events';
import { queryClient } from '@/data/client';
import { showAlert } from '@/utils/showAlert';
import usePlatform from '@/utils/hooks/usePlatform';

const TABS = [
    { key: 'recents', label: 'Recents', recentOnly: true, testID: 'picker-recents-tab' },
    { key: 'all', label: 'All Events', recentOnly: false, testID: 'picker-all-events-tab' },
];

const AddOccurrencePicker = ({ open, date, onClose }) => {
    const [activeTab, setActiveTab] = useState('recents');
    const [searchText, setSearchText] = useState('');
    const { isNative } = usePlatform();
    const { theme: { colors } } = useUnistyles();

    const recentOnly = activeTab === 'recents';
    const { data: events, isFetching } = Query.events.picker({ recentOnly });

    const createMutation = useMutation({
        mutationFn: createAdditionalEventInstance,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.events.all() });
            if (date) {
                queryClient.invalidateQueries({ queryKey: ['eventInstances', date.getMonth(), date.getFullYear()] });
            }
            onClose();
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteEvent,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.events.all() });
        },
    });

    const filteredEvents = useMemo(() => {
        if (!events) return [];
        if (!searchText.trim()) return events;
        const lower = searchText.toLowerCase().trim();
        return events.filter((event) => event.name?.toLowerCase().includes(lower));
    }, [events, searchText]);

    const handleAdd = useCallback((item) => {
        if (!date) return;
        createMutation.mutate({ eventID: item.id, date });
    }, [date, createMutation]);

    const handleDelete = useCallback((item) => {
        showAlert({
            title: 'Delete Event?',
            subtitle: `"${item.name}" and all its occurrences will be removed. This cannot be undone.`,
            isDestructive: true,
            confirmText: 'Delete',
            onConfirm: () => deleteMutation.mutate(item.id),
        });
    }, [deleteMutation]);

    const renderItem = useCallback(({ item }) => (
        <PickerRow
            item={item}
            onAdd={handleAdd}
            onDelete={handleDelete}
            isAdding={createMutation.isPending}
        />
    ), [handleAdd, handleDelete, createMutation.isPending]);

    // Gate heavy list behind `open` so nothing expensive renders while closed.
    if (!open) return null;

    return (
        <SheetModal
            title="Add Event Occurrence"
            isOpen={open}
            snapPoints={[700]}
            onCancel={onClose}
        >
            <View style={styles.header}>
                <View style={styles.segmentedControl}>
                    {TABS.map((tab) => {
                        const isActive = activeTab === tab.key;
                        return (
                            <Pressable
                                key={tab.key}
                                testID={tab.testID}
                                style={[styles.segment, isActive && styles.segmentActive]}
                                onPress={() => { setActiveTab(tab.key); setSearchText(''); }}
                            >
                                <Text style={[styles.segmentText, isActive && styles.segmentTextActive]}>
                                    {tab.label}
                                </Text>
                            </Pressable>
                        );
                    })}
                </View>

                <SearchBox
                    searchText={searchText}
                    onChangeText={setSearchText}
                    textChangeDelay={0}
                    placeholder="Search events..."
                />

                {date && (
                    <Text style={styles.subtitle}>
                        Select an event to create a new occurrence on{' '}
                        <Text style={styles.subtitleDate}>
                            {date.toLocaleString('default', { weekday: 'long', month: 'short', day: '2-digit', year: 'numeric' })}
                        </Text>
                    </Text>
                )}
            </View>

            <View style={styles.listContainer(isNative)}>
                {isFetching ? (
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator color={colors.events} size="large" />
                    </View>
                ) : filteredEvents.length === 0 ? (
                    <View style={styles.emptyContainer}>
                        <Unicon name="calendar-outline" size={40} colorVariant="textSecondary" />
                        <Text style={styles.emptyText}>
                            {searchText.trim() ? 'No events match your search' : 'No events found'}
                        </Text>
                    </View>
                ) : (
                    <LegendList
                        recycleItems
                        estimatedItemSize={65}
                        keyExtractor={(item) => item.id}
                        data={filteredEvents}
                        renderItem={renderItem}
                        keyboardShouldPersistTaps="handled"
                        contentContainerStyle={styles.listContent}
                    />
                )}
            </View>
        </SheetModal>
    );
};

const PickerRow = React.memo(({ item, onAdd, onDelete, isAdding }) => {
    return (
        <Pressable
            testID={`picker-event-${item.id}`}
            style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
            onPress={() => onAdd(item)}
            disabled={isAdding}
        >
            <View style={styles.rowInner(item.Calendar?.color)}>
                <Text numberOfLines={1} style={styles.rowTitle}>{item.name}</Text>
                <Text style={styles.rowSubtitle}>
                    {'Started on: ' + new Date(item.defaultStartTime).toLocaleDateString('default', { month: 'short', day: '2-digit', year: 'numeric' })}
                </Text>
            </View>
            <Pressable
                testID={`picker-delete-${item.id}`}
                style={styles.trashButton}
                onPress={() => onDelete(item)}
                hitSlop={8}
            >
                <Unicon name="trash-outline" size={18} colorVariant="error" />
            </Pressable>
        </Pressable>
    );
});

const styles = StyleSheet.create(({ colors, fonts }, { screen }) => ({
    header: {
        gap: 10,
        paddingBottom: 4,
    },
    segmentedControl: {
        flexDirection: 'row',
        backgroundColor: colors.background,
        borderRadius: 8,
        padding: 4,
        gap: 4,
    },
    segment: {
        flex: 1,
        paddingVertical: 8,
        alignItems: 'center',
        borderRadius: 6,
    },
    segmentActive: {
        backgroundColor: colors.events + '20',
    },
    segmentText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    segmentTextActive: {
        color: colors.events,
        fontFamily: fonts.bold,
    },
    subtitle: {
        color: colors.textSecondary,
        fontSize: 13,
        textAlign: 'center',
        paddingVertical: 4,
    },
    subtitleDate: {
        color: colors.text,
        fontFamily: fonts.bold,
    },
    listContainer: (isNative) => (isNative ? { height: screen.height * 0.65 } : { height: screen.height * 0.9 - 280 }),
    listContent: {
        paddingBottom: 80,
    },
    loadingContainer: {
        paddingVertical: 60,
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyContainer: {
        paddingVertical: 60,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
    },
    emptyText: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 8,
        marginBottom: 10,
        overflow: 'hidden',
    },
    rowPressed: {
        opacity: 0.7,
    },
    rowInner: (color) => ({
        borderLeftColor: color,
        borderLeftWidth: 5,
        paddingVertical: 8,
        paddingHorizontal: 12,
        flexGrow: 1,
        flex: 1,
    }),
    rowTitle: {
        color: colors.text,
        fontSize: 15,
        fontFamily: fonts.heading,
    },
    rowSubtitle: {
        fontSize: 12,
        color: colors.textSecondary,
        fontStyle: 'italic',
        marginTop: 2,
    },
    trashButton: {
        paddingHorizontal: 12,
        paddingVertical: 10,
    },
}));

export default AddOccurrencePicker;
